
  import OrderAssignmentsList from "@/views/Orders/Assignments/OrderAssignmentsList.vue";
  import ConfirmDialog from "@/components/ConfirmDialog.vue";
  import ConfirmDialogImpl from "@/components/ConfirmDialog.vue";
  import EmployeeLookup from "@/components/EmployeeLookup.vue";
  import {MessageTypes} from "@/components/message-types";
  import OrderServiceV2 from "@/service/OrderServiceV2";
  import {computed, defineComponent, onMounted, reactive, ref, toRefs} from "vue";
  import {OrderEmployeeAssignment, OrderEmployeeAssignmentType} from "@/model/OrderEmployeeAssignment";
  import {EmployeeV3Dto} from "@/service/EmployeeService";
  import eventBus from "@/eventbus";
  import {isAuthorized, t} from "@/mixin/mixins";
  import {orderId} from "@/RouterUtils";

  export default defineComponent({
    name: "OrdersAssignments",
    components: {
      OrderAssignmentsList: OrderAssignmentsList,
      ConfirmDialog: ConfirmDialog,
      EmployeeLookup: EmployeeLookup
    },
    setup() {

      const confirmDialog = ref<InstanceType<typeof ConfirmDialogImpl>>();

      const initialState = {
        // The v2 assignment API addresses the order by its uuid, not by its numeric id
        orderUuid: null as string | null,
        assignments: new Array<OrderEmployeeAssignment>()
      };

      const state = reactive({...initialState});

      const itemsAdmins = computed(() => state.assignments
        .filter(assignment => assignment.type == OrderEmployeeAssignmentType.SUPERVISOR));
      const itemsEmployees = computed(() => state.assignments
        .filter(assignment => assignment.type == OrderEmployeeAssignmentType.EMPLOYEE));
      const assignedEmployeeIds = computed(() => state.assignments.map(assignment => assignment.employee.id));
      const hasTeamLeader = computed(() => state.assignments.some(assignment => assignment.teamLeader));

      const load = () => {
        OrderServiceV2.getOrder(orderId.value)
          .then(response => {
            state.orderUuid = response.data.uuid ?? null;
            loadAssignments();
          })
          .catch(() => { });
      };

      const loadAssignments = () => {
        if (!state.orderUuid) {
          return;
        }
        OrderServiceV2.getOrderAssignments(state.orderUuid)
          .then(response => {
            state.assignments = response.data.assignments;
          });
      };

      const employeeSelected = (selected: EmployeeV3Dto | undefined, type: OrderEmployeeAssignmentType) => {
        if (!selected || !state.orderUuid) {
          return;
        }
        OrderServiceV2.updateOrderAssignment(state.orderUuid, selected.id, {type: type, teamLeader: false})
          .then(() => loadAssignments());
      };

      const deleteAssignment = (itemToDelete: OrderEmployeeAssignment) => {
        confirmDialog.value?.open("Löschen bestätigen", "Soll der Eintrag wirklich gelöscht werden?")
          .then(confirmResult => {
            if (confirmResult && state.orderUuid) {
              OrderServiceV2.deleteOrderAssignment(state.orderUuid, itemToDelete.employee.id)
                .then(() => {
                  loadAssignments();
                  eventBus.$emit(MessageTypes.SUCCESS, "Zuweisung entfernt!");
                });
            }
          });
      };

      const assignTeamLeader = (item: OrderEmployeeAssignment) => {
        if (!state.orderUuid) {
          return;
        }
        const request = {type: item.type, teamLeader: !item.teamLeader};
        OrderServiceV2.updateOrderAssignment(state.orderUuid, item.employee.id, request)
          .then(() => loadAssignments())
          .catch(() => {
            eventBus.$emit(MessageTypes.ERROR, t("errors.generalError"));
          });
      };

      onMounted(() => {
        load();
      });

      return {
        ...toRefs(state),
        confirmDialog,
        itemsAdmins,
        itemsEmployees,
        assignedEmployeeIds,
        hasTeamLeader,
        OrderEmployeeAssignmentType,
        assignTeamLeader,
        employeeSelected,
        deleteAssignment,
        isAuthorized
      };
    }
  });
